import { useReduxState } from 'hooks';
import { Avatar, Link } from 'components/common';

import type { AvatarProps } from 'components/common/Avatar';
import type { LinkInterface } from 'components/common/Link';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  avatarProps?: AvatarProps;
  children?: React.ReactNode;
  icon?: IconProp;
  iconStyle?: React.CSSProperties;
  iconTooltip?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
  title: string;
  titleLink?: LinkInterface;
  titleStyle?: React.CSSProperties;
  subtitleStyle?: React.CSSProperties;
}

const TitleWithIcon = ({
  avatarProps,
  children,
  icon,
  iconStyle,
  iconTooltip,
  onClick,
  style,
  title,
  titleLink,
  titleStyle,
  subtitleStyle
}: Props) => {
  const { theme } = useReduxState();

  return (
    <div className="title-with-icon" style={style}>
      {icon ? (
        <div
          className="icon"
          {...(iconTooltip
            ? { 'aria-label': iconTooltip, 'data-tooltip': iconTooltip }
            : {})}
          style={iconStyle}
        >
          <Avatar
            backgroundColor={theme.lightGreyDark}
            fontSize="1.4rem"
            icon={icon}
            padding="8px 0"
            size="32px"
            {...avatarProps}
          />
        </div>
      ) : null}
      <div className="content">
        <div className="title" style={titleStyle}>
          {onClick ? (
            <button className="title-link" onClick={onClick} tabIndex={0}>
              {title}
            </button>
          ) : titleLink ? (
            <Link
              as={titleLink.as}
              href={titleLink.href}
              linkColor="inherit"
              text={title}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-block',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                outline: 0,
                padding: 0
              }}
            />
          ) : (
            title
          )}
        </div>
        {children ? (
          <div className="subtitle" style={subtitleStyle}>
            {children}
          </div>
        ) : null}
      </div>

      <style jsx>{`
        .title-with-icon {
          display: flex;
          line-height: 1.2;
        }
        .icon {
          margin-right: 8px;
          margin-top: 1px;
        }
        .content {
          flex-grow: 1;
          min-width: 0;
        }
        .title {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
        }
        .title-link {
          background-color: transparent;
          border: none;
          color: inherit;
          cursor: pointer;
          display: inline-block;
          font-family: inherit;
          font-size: inherit;
          font-weight: inherit;
          outline: 0;
          padding: 0;
          text-align: left;
          text-decoration: none;
        }
        .title-link:focus,
        .title-link:hover {
          text-decoration: underline;
        }
        .subtitle {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
};

export default TitleWithIcon;
